import { domain } from '../auth/domain';

export const joinApiUrl = (baseUrl, endpoint) => {
  const normalizedBaseUrl = String(baseUrl || '').trim();
  if (!normalizedBaseUrl) {
    throw new Error('API base URL is required.');
  }

  let parsedBaseUrl;
  try {
    parsedBaseUrl = new URL(normalizedBaseUrl.endsWith('/') ? normalizedBaseUrl : `${normalizedBaseUrl}/`);
  } catch (error) {
    throw new Error('API base URL must be a valid absolute URL.');
  }

  if (!['http:', 'https:'].includes(parsedBaseUrl.protocol)) {
    throw new Error('API base URL must use HTTP or HTTPS.');
  }

  return new URL(String(endpoint || '').replace(/^\/+/, ''), parsedBaseUrl).toString();
};

const buildHeaders = (options = {}) => {
  const accessToken = localStorage.getItem('accessToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (accessToken) {
    headers.Authorization = `JWT ${accessToken}`;
  }

  return headers;
};

const requestJson = async (endpoint, options = {}) => {
  const response = await fetch(joinApiUrl(domain, endpoint), {
    ...options,
    headers: buildHeaders(options),
  });

  if (!response.ok) {
    let payload = null;
    let message = response.statusText || 'Request failed.';

    try {
      payload = await response.json();
      if (payload?.detail) {
        message = payload.detail;
      } else if (payload && typeof payload === 'object') {
        message = Object.entries(payload)
          .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join(' | ');
      }
    } catch (error) {
      payload = null;
    }

    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

export const normalizeExternalLoanApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const normalizeExternalLoanInterestRates = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.success === true && Array.isArray(payload.data)) return payload.data;
  return [];
};

export const getInterestRates = async () => normalizeExternalLoanInterestRates(
  await requestJson('external-loans/interest-rates/'),
);

export const submitLoan = (payload) => requestJson('external-loans/applications/', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const getLoanStatus = (referenceNumber) => requestJson(
  `external-loans/applications/${encodeURIComponent(referenceNumber)}/`,
);

export const getRecentApplications = async () => normalizeExternalLoanApiList(
  await requestJson('external-loans/applications/'),
);

export const decideExternalLoanApplication = (referenceNumber, decision, rejectionReason = '') => requestJson(
  `external-loans/applications/${encodeURIComponent(referenceNumber)}/approve/`,
  {
    method: 'POST',
    body: JSON.stringify({
      decision,
      rejection_reason: rejectionReason,
    }),
  },
);

export const getCurrentUserProfile = () => requestJson('profiles/users/me/');

export const getCurrentMemberships = () => requestJson('profiles/my-memberships/');

export const getGroupMemberDetails = (groupId, memberId) => {
  if (!groupId || !memberId) {
    throw new Error('Active group membership is required before applying for an external loan.');
  }
  return requestJson(`groups/${groupId}/members/${memberId}/`);
};

export const getGroupMembers = (groupId) => {
  if (!groupId) {
    throw new Error('Active group membership is required before applying for an external loan.');
  }
  return requestJson(`groups/${groupId}/members/`);
};

export const getExternalLoanApplicationSourceData = async (authUser = {}, options = {}) => {
  const [userProfile, membershipsPayload, providerInterestRates] = await Promise.all([
    getCurrentUserProfile(),
    getCurrentMemberships(),
    options.includeProviderInterestRates ? getInterestRates() : Promise.resolve([]),
  ]);
  const memberships = normalizeExternalLoanApiList(membershipsPayload);
  const activeMembership =
    memberships.find((membership) => membership.is_current) ||
    memberships.find((membership) => String(membership.id) === String(authUser.member_id)) ||
    (memberships.length === 1 ? memberships[0] : null);

  if (!activeMembership) {
    return {
      userProfile,
      memberships,
      memberDetails: null,
      groupMembers: [],
      providerInterestRates,
    };
  }

  const groupId = activeMembership.group_id || authUser.group_id;
  const memberId = activeMembership.id || authUser.member_id;
  const [memberDetails, groupMembersPayload] = await Promise.all([
    getGroupMemberDetails(groupId, memberId),
    getGroupMembers(groupId),
  ]);

  return {
    userProfile,
    memberships,
    memberDetails,
    groupMembers: normalizeExternalLoanApiList(groupMembersPayload),
    providerInterestRates,
  };
};
